import { standardDiagnosticCodes as defaultStandardDiagnosticCodes } from "./data";

const STORAGE_KEY = "safecheck-standard-diagnostic-codes";

export function loadStandardDiagnosticCodes(): StandardDiagnosticCode[] {
  if (typeof window === "undefined") return defaultStandardDiagnosticCodes;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultStandardDiagnosticCodes;
    const parsed = JSON.parse(stored) as StandardDiagnosticCode[];
    return Array.isArray(parsed) ? parsed : defaultStandardDiagnosticCodes;
  } catch {
    return defaultStandardDiagnosticCodes;
  }
}

export function saveStandardDiagnosticCodes(codes: StandardDiagnosticCode[]) {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
}

export function getStandardDiagnosticCodeById(id: string): StandardDiagnosticCode | undefined {
  return loadStandardDiagnosticCodes().find((item) => item.id === id);
}

export function createStandardDiagnosticCode(
  codes: StandardDiagnosticCode[],
  payload: Omit<StandardDiagnosticCode, "id">,
): StandardDiagnosticCode[] {
  const next: StandardDiagnosticCode = {
    id: String(Date.now()),
    ...payload,
  };
  const updated = [...codes, next];
  saveStandardDiagnosticCodes(updated);
  return updated;
}

export function updateStandardDiagnosticCode(
  codes: StandardDiagnosticCode[],
  id: string,
  payload: Omit<StandardDiagnosticCode, "id">,
): StandardDiagnosticCode[] {
  const updated = codes.map((item) => (item.id === id ? { ...item, ...payload } : item));
  saveStandardDiagnosticCodes(updated);
  return updated;
}

export function deleteStandardDiagnosticCode(
  codes: StandardDiagnosticCode[],
  id: string,
): StandardDiagnosticCode[] {
  const updated = codes.filter((item) => item.id !== id);
  saveStandardDiagnosticCodes(updated);
  return updated;
}

export function exportStandardDiagnosticCodesCsv(
  codes: StandardDiagnosticCode[],
  headers: Record<"code" | "name" | "type" | "description", string>,
) {
  const rows = [
    [headers.code, headers.name, headers.type, headers.description].join(","),
    ...codes.map((item) =>
      [item.code, item.label, item.type, item.description]
        .map((value) => `"${String(value).replaceAll('"', '""')}"`)
        .join(","),
    ),
  ];

  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "diagnostics-standards.csv";
  link.click();
  URL.revokeObjectURL(url);
}
